import React from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import AccumulationCalculator from './AccumulationCalculator';
import './Contract.css';

const TOC = [
  { id: 'fee-type',      label: 'Fee Type' },
  { id: 'payment-mode',  label: 'Payment Mode' },
  { id: 'billing-cycle', label: 'Billing Cycle' },
  { id: 'scheduling',    label: 'Scheduling' },
  { id: 'taxes',         label: 'Taxes' },
  { id: 'discounts',     label: 'Discounts' },
  { id: 'estimates',       label: 'Prepaid Estimates' },
  { id: 'accumulation',    label: 'Usage Accumulation' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ContractFeeComponents({ onNavigate }) {
  const { industry } = useIndustry();
  const c = industry.contract;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Contracts"
        title="Fee Settings"
        description="Fee settings control how each fee in a contract behaves — billing cycle, payment mode, taxes, and discounts — configured independently per fee."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'contract', label: 'Contracts' },
        ]}
      />

      <Section id="fee-type" title="Fee Type">
        <Para>
          When you add a fee to a contract you must choose its type. This controls how the billing
          engine determines what to charge.
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Type</th><th>How the charge is determined</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Usage-based</strong></td>
              <td>
                The charge is computed from meter events recorded during the billing period.
                The fee's rate card applies the configured pricing model to actual consumption.
              </td>
            </tr>
            <tr>
              <td><strong>Milestone-based</strong></td>
              <td>
                The fee has no rate card and no usage tracking. Instead, you define a sequence of
                milestones — each with a name and a fixed rate. A milestone is invoiced only when
                you manually mark it as ended. The charge appears in the billing period that
                contains the end date.
              </td>
            </tr>
          </tbody>
        </table>
        <Callout type="info">
          Milestone-based fees are independent of usage. They are common for implementation fees,
          professional services, or any staged deliverable where billing is tied to completion
          of defined phases rather than consumption.{' '}
          <button
            className="ctr-inline-link"
            onClick={() => onNavigate('milestone-fee')}
          >
            See Milestone-Based Fees →
          </button>
        </Callout>
      </Section>

      <Section id="payment-mode" title="Payment Mode">
        <Para>
          Each fee is either postpaid or prepaid — independently of every other fee in the contract.
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Mode</th><th>How it works</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Postpaid</strong></td>
              <td>Usage is tracked during the billing period. At period end, the pricing model runs on actual usage and generates an invoice.</td>
            </tr>
            <tr>
              <td><strong>Prepaid</strong></td>
              <td>The customer commits to an estimated usage amount upfront. The pricing model runs on the estimate at the start of the period. Actual usage is reconciled at period end.</td>
            </tr>
          </tbody>
        </table>
        <Callout type="info">
          A single contract can mix modes — one component postpaid, another prepaid.
          This is common when one service is predictable (prepaid) and another is variable (postpaid).
        </Callout>
      </Section>

      <Section id="billing-cycle" title="Billing Cycle">
        <Para>
          The billing cycle determines how often an invoice is generated for a component.
          Components in the same contract can bill on different cycles — they generate invoices independently.
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Cycle</th><th>Invoice frequency</th></tr>
          </thead>
          <tbody>
            <tr><td>Monthly</td><td>One invoice per calendar month</td></tr>
            <tr><td>Quarterly</td><td>One invoice every three months</td></tr>
            <tr><td>Annual</td><td>One invoice per year</td></tr>
          </tbody>
        </table>
        <Para>
          In this contract, <strong>{c.feeComponents[0]?.name}</strong> bills{' '}
          {c.feeComponents[0]?.billingCycle.toLowerCase()} while{' '}
          {c.feeComponents.length > 1
            ? <><strong>{c.feeComponents[1]?.name}</strong> bills {c.feeComponents[1]?.billingCycle.toLowerCase()}.</>
            : 'other components may bill on a different schedule.'
          }
        </Para>
      </Section>

      <Section id="scheduling" title="Scheduling">
        <Para>
          A fee can be active for only part of the contract's duration.
          If no schedule is set, it defaults to the full contract period.
        </Para>

        <SubSection title="Date-based schedule">
          <Para>
            Set explicit calendar start and end dates. Both are inclusive — a component ending
            on 31 Aug accumulates charges through the end of that day.
            Usage events outside the window are ignored for this component.
          </Para>
        </SubSection>

        <SubSection title="Cycle-based schedule">
          <Para>
            When the contract has no start date yet, the schedule is expressed as billing cycle
            numbers (e.g. cycles 3 – 5). Once a start date is assigned, the system resolves
            these automatically.
          </Para>
          <div className="ctr-resolution-example">
            <div className="ctr-res-row">
              <span className="ctr-res-label">Contract start</span>
              <span className="ctr-res-value">1 Apr 2024 (monthly)</span>
            </div>
            <div className="ctr-res-row">
              <span className="ctr-res-label">Schedule</span>
              <span className="ctr-res-value">Cycles 3 – 5</span>
            </div>
            <div className="ctr-res-divider">↓ resolves to</div>
            <div className="ctr-res-row ctr-res-row--resolved">
              <span className="ctr-res-label">Active period</span>
              <span className="ctr-res-value">1 Jun 2024 → 31 Aug 2024</span>
            </div>
          </div>
          <Callout type="info">
            Cycle 1 is the first full billing period after the contract start date.
            Cycle 3 starts on 1 Jun; the end of cycle 5 is 31 Aug — both inclusive.
          </Callout>
        </SubSection>
      </Section>

      <Section id="taxes" title="Taxes">
        <Para>
          Taxes available on a fee are defined at the product level.
          The contract selects which of those taxes apply to this specific customer.
          Different components in the same contract can have different tax treatment.
        </Para>
        <Callout type="info">
          Unchecking all taxes marks the component as tax-exempt for this customer.
          A common case is a customer who has submitted a valid exemption certificate.
        </Callout>
      </Section>

      <Section id="discounts" title="Discounts">
        <Para>
          Each fee has its own discount schedule — a list of period bands,
          each with a type and value. Discounts apply to the component's charge after
          the pricing model runs and after proration, but before taxes.
        </Para>
        <table className="ctr-table">
          <thead>
            <tr><th>Setting</th><th>Options</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Type</td>
              <td>Percentage of the charge, or a fixed amount</td>
            </tr>
            <tr>
              <td>Period bands</td>
              <td>
                Each band covers a range of billing periods. The last band is always
                open-ended (period n to ∞). Type and value can differ between bands.
              </td>
            </tr>
          </tbody>
        </table>
        <Callout type="info">
          A component with no discount schedule uses the rate card charge as-is.
          Discount type can change between bands — for example, a fixed introductory
          credit for the first few periods that switches to a percentage discount thereafter.
        </Callout>
      </Section>

      <Section id="estimates" title="Prepaid Estimates">
        <Para>
          Prepaid components require an upfront estimate for each meter × tag combination
          the component covers. The pricing model runs on these estimates to generate the first invoice.
        </Para>
        <Para>
          Estimates can be set at a finer granularity than the billing cycle. For example,
          a quarterly-billing component can have monthly estimates. The pricing model runs
          once per estimate period and the resulting costs are summed — not the usage.
        </Para>
        <Callout type="info">
          If usage is 100 per month and billing is quarterly, the invoice is
          cost(100) × 3, not cost(300). This matters when the pricing model has tiers —
          each month is evaluated independently against the tier boundaries.
        </Callout>
      </Section>

      <Section id="accumulation" title="Usage Accumulation">
        <Para>
          Normally each month's usage is priced on its own. The accumulation window lets you
          pool several months of usage together before pricing runs — which can push the
          combined total into a cheaper tier that no single month would reach alone.
        </Para>

        <table className="ctr-table">
          <thead>
            <tr><th>Setting</th><th>Controls</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Billing cycle</td>
              <td>When your customer receives an invoice</td>
            </tr>
            <tr>
              <td>Accumulation window</td>
              <td>How many months of usage are combined before the pricing model runs</td>
            </tr>
          </tbody>
        </table>

        <Callout type="info" title="Why this matters on tiered pricing">
          For flat per-unit rates, all three scenarios below produce the same total.
          The difference appears on Graduated or Volume pricing — pooling more usage into
          one pricing run may push the total into a cheaper tier that smaller individual
          inputs would never reach on their own.
        </Callout>

        <Para>
          The three scenarios below use the same example usage —{' '}
          <strong>Jan 100 units, Feb 200 units, Mar 300 units</strong> — so you can see
          exactly how the window changes the result.{' '}
          <em>In the formulas, <code>cost(X)</code> means "the charge for X units at your pricing rates."</em>
        </Para>

        <div className="ctr-accum-scenarios">

          <div className="ctr-accum-scenario">
            <div className="ctr-accum-scenario-header">
              <span className="ctr-accum-scenario-title">Price each month separately, bill quarterly</span>
              <div className="ctr-accum-settings">
                <span className="ctr-accum-setting">Billing cycle: <strong>quarterly</strong></span>
                <span className="ctr-accum-setting">Accumulation window: <strong>1 month</strong></span>
              </div>
            </div>
            <p className="ctr-accum-desc">
              Each month is priced on its own usage. The three amounts are collected internally and billed together in one invoice at the end of the quarter.
            </p>
            <div className="ctr-accum-steps">
              <div className="ctr-accum-step"><span className="ctr-accum-step-label">Jan</span><div className="ctr-formula">cost(100)</div></div>
              <div className="ctr-accum-step"><span className="ctr-accum-step-label">Feb</span><div className="ctr-formula">cost(200)</div></div>
              <div className="ctr-accum-step"><span className="ctr-accum-step-label">Mar</span><div className="ctr-formula">cost(300)</div></div>
              <div className="ctr-accum-step ctr-accum-step--total">
                <span className="ctr-accum-step-label">Q1 invoice</span>
                <div className="ctr-formula">cost(100) + cost(200) + cost(300)</div>
              </div>
            </div>
          </div>

          <div className="ctr-accum-scenario">
            <div className="ctr-accum-scenario-header">
              <span className="ctr-accum-scenario-title">Pool all usage, one invoice at quarter end</span>
              <div className="ctr-accum-settings">
                <span className="ctr-accum-setting">Billing cycle: <strong>quarterly</strong></span>
                <span className="ctr-accum-setting">Accumulation window: <strong>3 months</strong></span>
              </div>
            </div>
            <p className="ctr-accum-desc">
              Usage from all three months is added up first. The pricing model runs once on the combined total at quarter end. One invoice is generated.
            </p>
            <div className="ctr-accum-steps">
              <div className="ctr-accum-step"><span className="ctr-accum-step-label">Pool usage</span><div className="ctr-formula">100 + 200 + 300 = 600 units</div></div>
              <div className="ctr-accum-step ctr-accum-step--total">
                <span className="ctr-accum-step-label">Q1 invoice</span>
                <div className="ctr-formula">cost(600)</div>
              </div>
            </div>
          </div>

          <div className="ctr-accum-scenario">
            <div className="ctr-accum-scenario-header">
              <span className="ctr-accum-scenario-title">Pool usage cumulatively, invoice every month</span>
              <div className="ctr-accum-settings">
                <span className="ctr-accum-setting">Billing cycle: <strong>monthly</strong></span>
                <span className="ctr-accum-setting">Accumulation window: <strong>3 months</strong></span>
              </div>
            </div>
            <p className="ctr-accum-desc">
              An invoice goes out every month, but each invoice only charges the new usage at the cumulative rate. The pricing model runs on the running total and subtracts what was already billed.
            </p>
            <div className="ctr-accum-steps">
              <div className="ctr-accum-step">
                <span className="ctr-accum-step-label">Jan invoice</span>
                <div className="ctr-formula">cost(100)</div>
              </div>
              <div className="ctr-accum-step">
                <span className="ctr-accum-step-label">Feb invoice</span>
                <div className="ctr-formula">cost(100 + 200) − cost(100)</div>
              </div>
              <div className="ctr-accum-step">
                <span className="ctr-accum-step-label">Mar invoice</span>
                <div className="ctr-formula">cost(100 + 200 + 300) − cost(100 + 200)</div>
              </div>
            </div>
          </div>

        </div>

        <AccumulationCalculator />

      </Section>

    </DocPage>
  );
}
